import React, {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { callManager, CallManagerState } from "../call-manager";
import style from "./CustomCallView.module.css";

export interface CustomCallViewHandle {
  toggleMic: () => Promise<void>;
  toggleCamera: () => Promise<void>;
  switchCamera: () => Promise<void>;
  isMicOn: boolean;
  isCameraOn: boolean;
}

interface CustomCallViewProps {
  token: string;
  userId: string;
  roomId: string;
  displayName: string;
  waitingText?: string;
  onCallEnded?: () => void;
  onMicToggle?: (isMicOn: boolean) => void;
  onCameraToggle?: (isCameraOn: boolean) => void;
  onParticipantJoined?: (name: string) => void;
}

export const CustomCallView = forwardRef<CustomCallViewHandle, CustomCallViewProps>(
  ({ token, userId, roomId, displayName, waitingText, onCallEnded, onMicToggle, onCameraToggle, onParticipantJoined }, ref) => {
    const prevRemoteNameRef = useRef<string>("");
    const localVideoTargetRef = useRef<HTMLDivElement>(null);
    const remoteVideoTargetRef = useRef<HTMLDivElement>(null);

    const [callState, setCallState] = useState<CallManagerState>(callManager.getState());

    useImperativeHandle(ref, () => ({
      toggleMic: async () => {
        await callManager.toggleMic();
        onMicToggle?.(callManager.getState().isMicOn);
      },
      toggleCamera: async () => {
        await callManager.toggleCamera();
        onCameraToggle?.(callManager.getState().isCameraOn);
      },
      switchCamera: async () => {
        await callManager.switchCamera();
      },
      get isMicOn() { return callManager.getState().isMicOn; },
      get isCameraOn() { return callManager.getState().isCameraOn; },
    }));

    const propsRef = useRef({
      token,
      roomId,
      displayName,
      onCallEnded,
      onMicToggle,
      onCameraToggle,
      onParticipantJoined,
    });

    useEffect(() => {
      propsRef.current = {
        token,
        roomId,
        displayName,
        onCallEnded,
        onMicToggle,
        onCameraToggle,
        onParticipantJoined,
      };
    });

    const [connectionTimeout, setConnectionTimeout] = useState(false);
    const connectionTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Connection timeout effect
    useEffect(() => {
      const state = callState.callState;
      if (state === "initializing" || state === "connecting") {
        if (!connectionTimerRef.current) {
          connectionTimerRef.current = setTimeout(() => {
            setConnectionTimeout(true);
          }, 30000); // 30s
        }
      } else {
        if (connectionTimerRef.current) {
          clearTimeout(connectionTimerRef.current);
          connectionTimerRef.current = null;
        }
        setConnectionTimeout(false);
      }
      return () => {
        if (connectionTimerRef.current) {
          clearTimeout(connectionTimerRef.current);
          connectionTimerRef.current = null;
        }
      };
    }, [callState.callState]);

    useEffect(() => {
      // Subscribe to state changes from CallManager
      const unsubscribe = callManager.subscribe((state) => {
        setCallState((prev) => {
          if (prev.isMicOn !== state.isMicOn) {
            propsRef.current.onMicToggle?.(state.isMicOn);
          }
          if (prev.isCameraOn !== state.isCameraOn) {
            propsRef.current.onCameraToggle?.(state.isCameraOn);
          }
          return { ...state };
        });

        // Detect new remote participant joining
        if (state.remoteParticipantName && state.remoteParticipantName !== prevRemoteNameRef.current) {
          prevRemoteNameRef.current = state.remoteParticipantName;
          propsRef.current.onParticipantJoined?.(state.remoteParticipantName);
        }
      });

      // Register onCallEnded callback via multicast add/remove
      const handleEnded = () => {
        propsRef.current.onCallEnded?.();
      };
      callManager.addOnCallEnded(handleEnded);

      // Attach DOM nodes
      const attach = async () => {
        if (localVideoTargetRef.current && remoteVideoTargetRef.current) {
          await callManager.attachVideos(
            localVideoTargetRef.current,
            remoteVideoTargetRef.current
          );
        }
      };

      // Start the call only if not already active
      const init = async () => {
        const { token: t, roomId: r, displayName: d } = propsRef.current;
        if (!callManager.isActive()) {
          await callManager.joinRoom({ token: t, roomId: r, displayName: d });
        }
        await attach();
      };

      init();

      return () => {
        unsubscribe();
        callManager.removeOnCallEnded(handleEnded);
        // Detach DOM nodes only — call keeps running in CallManager
        callManager.detachVideos();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { isConnected, hasRemoteVideo, remoteParticipantName, isCameraOn } = callState;
    const isInitializing =
      callState.callState === "initializing" || callState.callState === "connecting";

    const [isSwapped, setIsSwapped] = useState(false);

    const handleSwap = () => {
      setIsSwapped((prev) => !prev);
    };

    return (
      <div className={style.callWrapper}>
        {/* Remote video — fills screen by default, becomes PIP when swapped */}
        <div
          className={`${isSwapped ? style.localVideoPip : style.remoteVideoOuter} ${isSwapped ? style.pipClickable : ""}`}
          onClick={isSwapped ? handleSwap : undefined}
        >
          <div className={style.remoteVideoTarget} ref={remoteVideoTargetRef} />
          {isInitializing && (
            <div className={style.overlay}>
              {connectionTimeout ? (
                <>
                  <span className="material-icons-round" style={{ fontSize: "48px", color: "var(--error)" }}>error_outline</span>
                  <span style={{ marginTop: "8px", fontSize: "14px", fontWeight: 500 }}>Connection timed out</span>
                  <span style={{ fontSize: "12px", color: "var(--on-surface-muted)", textAlign: "center", padding: "0 20px", marginTop: "4px" }}>
                    Please check your internet connection and try rejoining the call.
                  </span>
                </>
              ) : (
                <>
                  <div className={style.connectingSpinner} />
                  <span>Connecting...</span>
                </>
              )}
            </div>
          )}
          {isConnected && !hasRemoteVideo && (
            <div className={style.overlay}>
              <span
                className="material-icons-round"
                style={{ fontSize: "52px", opacity: 0.25 }}
              >
                person
              </span>
              <span style={{ marginTop: "8px", fontSize: "13px" }}>
                {remoteParticipantName || waitingText || "Waiting for participant..."}
              </span>
            </div>
          )}
          {remoteParticipantName && hasRemoteVideo && !isSwapped && (
            <div className={style.remoteNameBadge}>{remoteParticipantName}</div>
          )}
        </div>

        {/* Local video PIP — top right by default, fills screen when swapped */}
        <div
          className={`${isSwapped ? style.remoteVideoOuter : style.localVideoPip} ${!isSwapped ? style.pipClickable : ""}`}
          onClick={!isSwapped ? handleSwap : undefined}
        >
          <div className={style.localVideoTarget} ref={localVideoTargetRef} />
          {!isCameraOn && (
            <div className={style.cameraOffOverlay}>
              <span className="material-icons-round">videocam_off</span>
            </div>
          )}
          {remoteParticipantName && hasRemoteVideo && isSwapped && (
            <div className={style.remoteNameBadge}>{remoteParticipantName}</div>
          )}
        </div>
      </div>
    );
  }
);

CustomCallView.displayName = "CustomCallView";
